import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { buildCartId, Cart } from "../types/Cart";
import { SearchableInvestorEntry } from "../types/SearchableInvestorEntry";
import { useAppAuthState } from "./use-app-auth-state";
import { SearchParams } from "./use-searchable-investor-search-results";

export interface CartContextValue {
  cart: Cart | null;
  isCartEmpty: boolean;
  numItemsInCart: number;
  clearCart: () => void;
  clearCartWithConfirmation: () => void;
  addToCart: (investor: SearchableInvestorEntry) => void;
  addSearchToCart: (search: SearchParams) => void;
  setColumnOrder: (columnOrder: string[]) => void;
}

export function useProvideCart(): CartContextValue {
  const [cart, setLocalCart] = useState<Cart | null>(null);
  const { fireUser } = useAppAuthState();

  const cartItems = useMemo(() => {
    return cart?.items ?? [];
  }, [cart]);

  useEffect(() => {
    if (fireUser && cart && !cart?.creatorUserId) {
      setLocalCart({
        ...cart,
        creatorUserId: fireUser.uid,
      });
    }
  }, [fireUser, cart]);

  useEffect(() => {
    const cart = localStorage.getItem("cart");
    if (cart) {
      setLocalCart(JSON.parse(cart) as Cart);
    }
  }, []);

  useEffect(() => {
    if (cart) {
      localStorage.setItem("cart", JSON.stringify(cart));
    } else {
      localStorage.removeItem("cart");
    }
  }, [cart]);

  const setColumnOrder = useCallback(
    (columnOrder: string[]) => {
      if (cart) {
        setLocalCart({ ...cart, columnOrder });
      }
    },
    [cart],
  );

  const clearCart = useCallback(() => {
    setLocalCart(null);
  }, []);

  const clearCartWithConfirmation = useCallback(() => {
    if (
      cartItems.length < 5 ||
      window.confirm("Are you sure you want to clear your cart?")
    ) {
      clearCart();
    }
  }, [cartItems, clearCart]);

  const addSearchToCart = useCallback((_search: SearchParams) => {
    toast.error(`This functionality is coming soon!`);
  }, []);

  const addToCart = useCallback(
    (investor: SearchableInvestorEntry) => {
      if (!fireUser) {
        toast.error("Please login to add items to your cart");
        return;
      }
      if (cart?.searchableInvestorEntryIds?.includes(investor.id)) {
        toast.error(`Investor (${investor.name}) already in cart`);
        return;
      }

      const items = [
        ...(cart?.items ?? []),
        {
          id: Math.random().toString(),
          searchableInvestorEntry: investor,
        },
      ];
      const searchableInvestorEntryIds = items.map(
        (i) => i.searchableInvestorEntry.id,
      );
      const now = new Date().getTime();
      setLocalCart({
        id: buildCartId(now),
        startedDate: cart?.startedDate ?? now,
        items,
        searchableInvestorEntryIds,
        status: "active",
        creatorUserId: fireUser?.uid,
        columnOrder: [],
        exportFormat: "csv",
      });
      toast.success(`${investor.name || "Investor"} added to cart`);
    },
    [cart, fireUser],
  );

  const numItemsInCart = cartItems.length;
  return useMemo(
    () => ({
      cart,
      isCartEmpty: cartItems.length === 0,
      numItemsInCart,
      clearCart,
      clearCartWithConfirmation,
      addToCart,
      addSearchToCart,
      setColumnOrder,
    }),
    [
      cart,
      cartItems.length,
      numItemsInCart,
      clearCart,
      clearCartWithConfirmation,
      addToCart,
      addSearchToCart,
      setColumnOrder,
    ],
  );
}
